import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import styles from './DesignSystem.module.css';
import { FormikDropdown } from '../../components/form-components/common-components/dropdown/FormikDropdown';
import { FormikTextField } from '../../components/form-components/common-components/text-field/FormikTextField';
import { FormikMultiSelect } from '../../components/form-components/common-components/multiselect/FormikMultiselect';
import CommonDropdown from '../../components/common-components/dropdown/CommonDropdown';
import { MultiSelect } from '../../components/common-components/multiselect/CommonMultiselect';
import { TextField } from '../../components/common-components/text-field/TextField';
import CommonButton from '../../components/common-components/button/CommonButton';

const DesignSystem = () => {
  const [values, setValues] = useState({
    text: '',
    email: '',
    password: '',
    number: '',
    time: '',
    country: '',
    frameworks: [],
  });

  const dropdownOptions = [
    { value: 'tr', label: 'Turkey' },
    { value: 'us', label: 'United States' },
    { value: 'uk', label: 'United Kingdom' },
    { value: 'de', label: 'Germany' },
    { value: 'fr', label: 'France' },
  ];

  const multiSelectOptions = [
    { value: 'react', label: 'React' },
    { value: 'vue', label: 'Vue' },
    { value: 'angular', label: 'Angular' },
    { value: 'svelte', label: 'Svelte' },
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValues(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleDropdownChange = (name: string) => (value: string | number | any) => {
    setValues(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <div className={styles.container}>
      <h1>Design System</h1>

      <section className={styles.section}>
        <h2>Formik Components</h2>

        <div className={styles.componentSection}>
          <h3>Text Inputs</h3>
          <Formik initialValues={values} onSubmit={values => console.log(values)}>
            {() => (
              <Form className={styles.form}>
                <FormikTextField name="text" label="Text" type="text" placeholder="Enter text" />
                <FormikTextField
                  name="email"
                  label="Email"
                  type="email"
                  placeholder="Enter email"
                />
                <FormikTextField
                  name="password"
                  label="Password"
                  type="password"
                  placeholder="Enter password"
                />
                <FormikTextField
                  name="number"
                  label="Number"
                  type="number"
                  placeholder="Enter number"
                />
                <FormikTextField name="time" label="Time" type="time" />
              </Form>
            )}
          </Formik>
        </div>
        <div className={styles.componentSection}>
          <h3>Text Input with Suggestions</h3>
          <Formik initialValues={values} onSubmit={values => console.log(values)}>
            {() => (
              <Form className={styles.form}>
                <FormikTextField
                  name="textWithSuggestion"
                  label="Text with Suggestions"
                  placeholder="Type to see suggestions"
                  fieldToSearch="demo"
                  onSuggestionSearch={async (fieldName, searchTerm) => {
                    const demoData = [
                      'Apple',
                      'Apricot',
                      'Avocado',
                      'Banana',
                      'Blackberry',
                      'Blueberry',
                      'Cherry',
                      'Coconut',
                      'Cranberry',
                    ];

                    // Filter demo data based on search term
                    const filtered = demoData.filter(item =>
                      item.toLowerCase().includes(searchTerm.toLowerCase())
                    );

                    // Simulate API delay
                    await new Promise(resolve => setTimeout(resolve, 300));

                    return filtered.slice(0, 5);
                  }}
                />
              </Form>
            )}
          </Formik>
        </div>
        <div className={styles.componentSection}>
          <h3>Dropdowns</h3>
          <Formik initialValues={values} onSubmit={values => console.log(values)}>
            {() => (
              <Form className={styles.form}>
                <FormikDropdown
                  name="country"
                  label="Country"
                  options={dropdownOptions}
                  placeholder="Select country"
                />
                <FormikMultiSelect
                  name="frameworks"
                  label="Frameworks"
                  options={multiSelectOptions}
                  placeholder="Select frameworks"
                />
                <button type="submit" className={styles.submitButton}>
                  Submit
                </button>
              </Form>
            )}
          </Formik>
        </div>
      </section>

      <section className={styles.section}>
        <h2>Regular Components</h2>

        <div className={styles.componentSection}>
          <h3>Text Inputs</h3>
          <div className={styles.form}>
            <TextField
              name="text"
              label="Text"
              type="text"
              value={values.text}
              onChange={handleInputChange}
              placeholder="Enter text"
            />
            <TextField
              name="email"
              label="Email"
              type="email"
              value={values.email}
              onChange={handleInputChange}
              placeholder="Enter email"
            />
            <TextField
              name="password"
              label="Password"
              type="password"
              value={values.password}
              onChange={handleInputChange}
              placeholder="Enter password"
            />
            <TextField
              name="number"
              label="Number"
              type="number"
              value={values.number}
              onChange={handleInputChange}
              placeholder="Enter number"
            />
            <TextField
              name="time"
              label="Time"
              type="time"
              value={values.time}
              onChange={handleInputChange}
            />
          </div>
        </div>

        <div className={styles.componentSection}>
          <h3>Dropdowns</h3>
          <div className={styles.form}>
            <CommonDropdown
              value={values.country}
              onChange={handleDropdownChange('country')}
              options={dropdownOptions}
              label="Country"
              placeholder="Select country"
            />
            <MultiSelect
              value={values.frameworks}
              onChange={handleDropdownChange('frameworks')}
              options={multiSelectOptions}
              label="Frameworks"
              placeholder="Select frameworks"
            />
          </div>
        </div>
      </section>
      <section className={styles.section}>
        <h2>Button Components</h2>

        <div className={styles.componentSection}>
          <h3>Button Types</h3>
          <div className={styles.form}>
            <CommonButton type="primary">Primary Button</CommonButton>
            <CommonButton type="secondary">Secondary Button</CommonButton>
            <CommonButton type="tertiary">Tertiary Button</CommonButton>
            <CommonButton type="outline">Outline Button</CommonButton>
          </div>

          <h3>Disabled Buttons</h3>
          <div className={styles.form}>
            <CommonButton type="primary" disabled>
              Disabled Primary
            </CommonButton>
            <CommonButton type="secondary" disabled>
              Disabled Secondary
            </CommonButton>
            <CommonButton type="tertiary" disabled>
              Disabled Tertiary
            </CommonButton>
            <CommonButton type="outline" disabled>
              Disabled Outline
            </CommonButton>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DesignSystem;
